import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, Palette, Globe, ToggleLeft, ToggleRight, CreditCard, AlertTriangle } from 'lucide-react';
import { getSiteSettings, updateSiteSettings } from '../../services/api';
import api from '../../services/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resettingPlans, setResettingPlans] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await getSiteSettings();
      setSettings(res.data || getDefaultSettings());
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings(getDefaultSettings());
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubscriptionPlans = async () => {
    if (!window.confirm('This will reset ALL subscription plans to:\n\n• Free ($0)\n• Premium ($4/month)\n• Creator (Apply Only)\n\nContinue?')) {
      return;
    }
    
    setResettingPlans(true);
    try {
      const res = await api.post('/subscriptions/plans/reset');
      toast.success(res.data.message || 'Subscription plans reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset plans');
    } finally {
      setResettingPlans(false);
    }
  };

  const getDefaultSettings = () => ({
    site_name: 'Supremics',
    site_tagline: 'Where Comics Come Alive',
    primary_color: '#E63946',
    secondary_color: '#FF6B35',
    social_links: { twitter: '', instagram: '', discord: '', youtube: '' },
    features: {
      donations_enabled: true,
      feedback_enabled: true,
      ai_tools_enabled: true,
      signups_enabled: true,
      weekly_prizes_enabled: true
    },
    weekly_prize_amount: 50.00,
    donation_max: 2.00,
    maintenance_mode: false,
    footer_text: '© 2025 Supremics. All rights reserved.',
    analytics_id: ''
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateSiteSettings(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateFeature = (key, value) => {
    setSettings({
      ...settings,
      features: { ...settings.features, [key]: value }
    });
  };

  const updateSocialLink = (key, value) => {
    setSettings({
      ...settings,
      social_links: { ...settings.social_links, [key]: value }
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-teko">Site Settings</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 animate-pulse">
              <div className="h-5 w-1/3 bg-[var(--border)] rounded mb-4" />
              <div className="h-10 w-full bg-[var(--border)] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-teko">Site Settings</h2>
        <Button onClick={handleSave} disabled={saving}>
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-lg font-teko mb-4 flex items-center gap-2">
            <Globe size={18} />
            General
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm mb-1">Site Name</label>
              <input
                type="text"
                value={settings.site_name}
                onChange={(e) => setSettings({...settings, site_name: e.target.value})}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Tagline</label>
              <input
                type="text"
                value={settings.site_tagline}
                onChange={(e) => setSettings({...settings, site_tagline: e.target.value})}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Footer Text</label>
              <input
                type="text"
                value={settings.footer_text}
                onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-lg font-teko mb-4 flex items-center gap-2">
            <Palette size={18} />
            Appearance
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm mb-1">Primary Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                  className="flex-1 px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm mb-1">Secondary Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                  className="flex-1 px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-lg font-teko mb-4">Social Links</h3>
          <div className="space-y-4">
            {Object.entries(settings.social_links || {}).map(([key, value]) => (
              <div key={key}>
                <label className="block text-sm mb-1 capitalize">{key}</label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => updateSocialLink(key, e.target.value)}
                  placeholder={`https://${key}.com/...`}
                  className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-lg font-teko mb-4">Features</h3>
          <div className="space-y-4">
            {Object.entries(settings.features || {}).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                <button
                  onClick={() => updateFeature(key, !value)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    value ? 'bg-green-500' : 'bg-gray-500'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Monetization */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-lg font-teko mb-4">Monetization</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm mb-1">Weekly Prize Amount ($)</label>
              <input
                type="number"
                step="1"
                value={settings.weekly_prize_amount}
                onChange={(e) => setSettings({...settings, weekly_prize_amount: parseFloat(e.target.value)})}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Max Donation ($)</label>
              <input
                type="number"
                step="0.5"
                value={settings.donation_max}
                onChange={(e) => setSettings({...settings, donation_max: parseFloat(e.target.value)})}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Analytics */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-lg font-teko mb-4">Analytics</h3>
          <div>
            <label className="block text-sm mb-1">Google Analytics ID</label>
            <input
              type="text"
              value={settings.analytics_id}
              onChange={(e) => setSettings({...settings, analytics_id: e.target.value})}
              placeholder="UA-XXXXXXXXX-X or G-XXXXXXXXXX"
              className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
            />
          </div>
        </div>

        {/* Subscription Plans Management */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-lg font-teko mb-4 flex items-center gap-2">
            <CreditCard size={18} />
            Subscription Plans
          </h3>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Reset subscription plans to the default configuration:
          </p>
          <ul className="text-sm text-[var(--text-secondary)] mb-4 space-y-1">
            <li>• <strong>Free</strong> - $0 (browse & read free chapters)</li>
            <li>• <strong>Premium</strong> - $4/month (unlock all chapters)</li>
            <li>• <strong>Creator</strong> - Apply only (publishing tools)</li>
          </ul>
          <button
            onClick={handleResetSubscriptionPlans}
            disabled={resettingPlans}
            className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="reset-plans-btn"
          >
            {resettingPlans ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                Reset Subscription Plans
              </>
            )}
          </button>
          <p className="text-xs text-yellow-500 mt-2 flex items-center gap-1">
            <AlertTriangle size={12} />
            This will delete all existing plans and create new defaults
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;
